/**
 * The one resolver for course workspace addresses (#1393 M4,
 * `docs/delivery/opus/DESK-FIRST.md` contract 1).
 *
 * After M4 the course has three primary destinations (Desk, Coursework,
 * Gradebook or Grades), two header destinations (People, Manage course) and
 * All notices. Sessions, the plan and Materials are Desk's modes and parts.
 * Every older address keeps resolving, permanently: this function maps an
 * arrival's query to its canonical query in one step, so the first replace is
 * the final one (rule 1), and a canonical query maps to itself (rule 2).
 *
 * It reads only the query. It never awaits a read: the two owner-resolved
 * selectors (a material id, a learner's bare assignment) resolve to an address
 * whose owner finishes the job (rule 6). It grants nothing; every owner still
 * checks the course, the tenant and the person.
 */
import { COURSE_ADDRESS_VIEWS } from "./course-workspace";

/** How a canonical course address renders. */
export type CourseAddressFamily = "desk" | "session" | "view";

export type CourseArrival =
  | { kind: "canonical"; family: CourseAddressFamily }
  /** Replace the arrival with this query (no leading `?`; empty is the bare Desk). */
  | { kind: "rewrite"; search: string }
  /** Rule 4: no rewrite; the frame says "Course view unavailable" with Return to Desk. */
  | { kind: "invalid" };

const VIEWS = new Set<string>(COURSE_ADDRESS_VIEWS);
/** Views retired into Desk: the bare course, its Calendar order and its Resources part. */
const RETIRED = new Set(["desk", "home", "sessions", "plan", "materials"]);
/** Each appears at most once with a non-empty value (rule 4). */
const SINGLE = ["view", "session", "class", "plan", "material", "compose", "assignment", "owner", "submission", "change", "unit", "order", "part", "open", "tab"] as const;

/**
 * The query with `lead` first, then every remaining parameter in arrival order
 * (rule 7). A query that already carries `lead` keeps its own order, so a
 * canonical address is never rewritten for order alone (rule 2).
 */
function led(params: URLSearchParams, lead: Record<string, string>): URLSearchParams {
  if (Object.entries(lead).every(([key, value]) => params.getAll(key).length === 1 && params.get(key) === value)) return params;
  const next = new URLSearchParams(lead);
  for (const [key, value] of params) if (!(key in lead)) next.append(key, value);
  return next;
}

/** Desk's Calendar order and its rows: a session as a row's context, its change task, a plan. */
const CALENDAR_SELECTORS = ["order", "session", "change", "plan"] as const;
/** Desk's Resources part and what its owner finishes: a material, adding one, its session. */
export const RESOURCES_SELECTORS = ["part", "material", "compose", "unit"] as const;

/**
 * The Desk query in one mode. Each mode's selectors leave with it: outside
 * Calendar order a session names the session's own page and a change or plan
 * has no host (#1393 M4-A1); outside Outline the Resources part has none.
 */
export function deskModeSearch(params: URLSearchParams, mode: "outline" | "calendar"): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of mode === "calendar" ? RESOURCES_SELECTORS : CALENDAR_SELECTORS) next.delete(key);
  if (mode === "calendar") next.set("order", "calendar");
  return next;
}

function familyOf(params: URLSearchParams): CourseAddressFamily {
  if (params.has("view")) return "view";
  return params.has("session") && params.get("order") !== "calendar" ? "session" : "desk";
}

/**
 * Resolve one course workspace query for a shell's perspective: teaching for
 * `/instructor` and `/admin/teaching`, learning for `/student`.
 */
export function resolveCourseArrival(teaching: boolean, search: URLSearchParams | string): CourseArrival {
  const arrival = new URLSearchParams(search);
  const finish = (next: URLSearchParams): CourseArrival =>
    next.toString() === arrival.toString() ? { kind: "canonical", family: familyOf(next) } : { kind: "rewrite", search: next.toString() };
  const params = new URLSearchParams(arrival);

  // Rule 4: validate the whole arrival before choosing a destination, so no
  // mapping consumes a selector it would have refused (#1393 M4-A4). Only the
  // notice family's own combinations are All notices' to refuse (R39).
  const notice = params.has("notice") || params.getAll("compose").includes("notice");
  for (const key of SINGLE) {
    if (notice && key === "compose") continue;
    const values = params.getAll(key);
    if (values.length > 1 || values.length === 1 && values[0] === "") return { kind: "invalid" };
  }
  const compose = params.get("compose");
  if (!notice && compose !== null && compose !== "material") return { kind: "invalid" };
  const cls = params.get("class");
  const session = params.get("session");
  if (cls !== null && session !== null && cls !== session) return { kind: "invalid" };

  const view = params.get("view");
  const retired = view !== null && RETIRED.has(view) ? view : null;
  if (view !== null && !retired && (!VIEWS.has(view) || view === "settings" && !teaching)) return { kind: "invalid" };
  const canonicalView = view !== null && !retired;
  const consume = (...keys: string[]) => { for (const key of keys) params.delete(key); };

  // A notice, or composing one, is All notices' (R38, R39): forwarded with
  // its selectors, and that page refuses an ambiguous combination.
  if (notice) {
    consume("view");
    return finish(led(params, { view: "notices" }));
  }

  // Rule 5, first match wins. `visit` is kept throughout: the rest of the
  // address maps by its own row.

  // Exact work consumes a retired view (R52-R61). A teacher's native work is
  // the Coursework register's row (R34); a learner's bare work is resolved by
  // the frame's own read (R35, R60); a provider record is a sheet (R36).
  // Each exact selector consumes the lower-precedence routing intent in the
  // same pass, so the first replace is the final one (rule 1, M4-A5).
  if (params.has("assignment")) {
    consume("open");
    if (retired) consume("view");
    if (retired === "materials") consume("session");
    // A plan row's class is still the work's session context (R8, R9).
    if (cls !== null) {
      consume("class", "plan");
      params.set("session", cls);
    }
    if (teaching && !params.has("owner") && !params.has("view")) return finish(led(params, { view: "coursework" }));
    return finish(params);
  }

  // A plan's class is its session (R8, R9).
  if (cls !== null) {
    consume("class", "plan", "open");
    if (retired) consume("view");
    params.set("session", cls);
    return finish(params);
  }

  // A session's change task, an unplaced plan: rows of Calendar order (R11-R14).
  // An open visit is hosted in Outline (R62), so a stored row with a visit and
  // no order comes to rest there, its row selectors leaving with the order.
  if (!canonicalView && (session !== null && params.has("change") || params.has("plan"))) {
    consume("view", "open");
    return finish(params.has("visit") && !params.has("order") ? deskModeSearch(params, "outline") : led(params, { order: "calendar" }));
  }

  // A material, or adding one, belongs to the Resources owner, which opens the
  // material's visit once its library is read (R16, R17, R48-R50). A session
  // is context it ignores, so it is consumed rather than made exact (rule 9).
  // The retired `materials` view is the same Resources part.
  if (!canonicalView && (params.has("material") || compose === "material" || retired === "materials")) {
    consume("view", "open");
    return finish(led(deskModeSearch(params, "outline"), { part: "resources" }));
  }

  if (retired) {
    consume("view", "open");
    // An open visit is hosted in Outline, so a retired view that would choose
    // Calendar order yields to the visit's host (R62, as `TheCourse` does).
    const calendar = !params.has("visit");
    switch (retired) {
      case "sessions":
        // An exact session wins (R6); without one, the sessions are Calendar order (R5).
        return finish(session !== null || !calendar ? params : led(params, { order: "calendar" }));
      case "plan":
        // The plan's session is a Calendar order row, never the exact session (R10).
        return finish(calendar ? led(params, { order: "calendar" }) : deskModeSearch(params, "outline"));
      default:
        // `desk` and the retired `home` are the bare course (R2-R4).
        return finish(params);
    }
  }

  // The retired `open` parameter (R21-R24): a view the arrival already names
  // wins; an unknown value is left alone.
  const open = params.get("open");
  if (open !== null) {
    if (canonicalView) {
      consume("open");
      return finish(params);
    }
    if (open === "attendance") {
      consume("open");
      // A learner's own attendance is in Grades; a teacher's roll is in People (R1).
      return finish(led(params, { view: teaching ? "people" : "grades", part: "attendance" }));
    }
    if (open === "gradebook") {
      consume("open");
      return finish(led(params, { view: "grades" }));
    }
  }
  return finish(params);
}

/** The canonical query for an arrival, or the arrival itself when it is canonical or refused. */
export function canonicalCourseSearch(teaching: boolean, search: URLSearchParams | string): string {
  const resolved = resolveCourseArrival(teaching, search);
  return resolved.kind === "rewrite" ? resolved.search : new URLSearchParams(search).toString();
}
